import React, { useState } from "react";
import emailjs from "emailjs-com";
import { motion } from "framer-motion";
import { ArrowRight, BarChart3, FileText, Presentation, Target, CheckCircle, Brain, Layers, ActivitySquare } from "lucide-react";
import "./styles/globals.css";

const motionConfig = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7 },
};

const outputs = [
  {
    icon: <BarChart3 size={34} />,
    title: "Executive Dashboard™",
    description: "A clear readiness scorecard showing current maturity, target maturity, captured value, and remaining opportunity.",
  },
  {
    icon: <FileText size={34} />,
    title: "Executive Report™",
    description: "A board-ready PDF with maturity progression, executive risks, opportunities, value journey, and 90-day plan.",
  },
  {
    icon: <Presentation size={34} />,
    title: "Executive Deck™",
    description: "A PowerPoint transformation review designed for leadership teams, steering committees, and board discussions.",
  },
  {
    icon: <Target size={34} />,
    title: "90-Day Transformation Plan™",
    description: "A practical roadmap translating AI readiness gaps into immediate execution priorities.",
  },
];

const domains = [
  "Data Readiness",
  "Technology Readiness",
  "Governance Readiness",
  "Organizational Readiness",
  "Delivery Readiness",
];

export default function AIReadinessPage() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    title: "",
    email: "",
    initiative: "",
  });

  const handleAssessmentSubmit = (e) => {
    e.preventDefault();

    emailjs.send(
      "service_w9ntyqt",
      "template_k2nfhm7",
      {
        user_name: form.name,
        user_email: form.email,
        user_company: form.company,
        user_title: form.title,
        message: `
AI READINESS ASSESSMENT LEAD

Name:
${form.name}

Email:
${form.email}

Company:
${form.company}

Title:
${form.title}

Current AI Initiative:
${form.initiative}

Source:
BioPM AI Readiness Landing Page
`,
        to_email: "info@biopm.com",
      },
      "a1q0fJffNbDQKyOje"
    ).then(() => {
      alert("Thank you. Your AI Readiness Assessment inquiry has been received. A BioPM representative will contact you shortly.");
      setForm({
        name: "",
        company: "",
        title: "",
        email: "",
        initiative: "",
      });
    }).catch((error) => {
      console.error("EmailJS error:", error);
      alert("There was an error submitting your inquiry. Please email info@biopm.com directly.");
    });
  };

  return (
    <div className="min-h-screen bg-white font-ageo text-dark-teal">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="text-2xl font-bold text-dark-teal no-underline">
            BioPM
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold">
            <a href="/#services" className="hover:text-primary-teal">Services</a>
            <a href="/ai-readiness" className="text-primary-teal">AI Readiness™</a>
            <a href="/#curepath" className="hover:text-primary-teal">CurePath™</a>
            <a href="/#contact" className="hover:text-primary-teal">Contact</a>
          </nav>
          <a
            href="mailto:info@biopm.com?subject=AI%20Readiness%20Assessment%20Inquiry"
            className="px-5 py-2 rounded-full bg-primary-teal text-dark-teal font-bold no-underline"
          >
            Request Assessment
          </a>
        </div>
      </header>

      <main className="pt-24">
        <section className="relative overflow-hidden bg-dark-teal text-white px-6 py-28">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,#00D1AF,transparent_35%)]" />
          <motion.div className="max-w-6xl mx-auto relative" {...motionConfig}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-teal/15 text-primary-teal border border-primary-teal/30 text-sm font-bold mb-8">
              <Brain size={18} /> BioPM AI Readiness Framework™
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold leading-tight max-w-5xl">
              Assess your organization&apos;s readiness for the AI era.
            </h1>

            <p className="mt-8 text-xl md:text-2xl text-white/70 max-w-4xl leading-relaxed">
              Identify operational gaps, prioritize transformation opportunities, and receive an Executive Report™,
              Executive Deck™, and 90-Day Transformation Plan™ tailored to your drug development organization.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:info@biopm.com?subject=AI%20Readiness%20Assessment%20Inquiry"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-teal text-dark-teal font-bold no-underline"
              >
                Request Assessment <ArrowRight size={20} />
              </a>
              <a
                href="#outputs"
                className="inline-flex items-center justify-center px-8 py-4 rounded-full border border-white/20 text-white font-bold no-underline"
              >
                View Sample Outputs
              </a>
            </div>
          </motion.div>
        </section>

        <section className="px-6 py-24 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div className="text-center mb-14" {...motionConfig}>
              <h2 className="text-4xl md:text-5xl font-bold">From assessment to executive action.</h2>
              <p className="mt-5 text-lg text-gray-600 max-w-3xl mx-auto">
                BioPM AI Readiness™ turns organizational input into a clear transformation roadmap leadership can act on.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                ["01", "Assess", "50-question assessment across Data, Technology, Governance, Organization, and Delivery."],
                ["02", "Analyze", "Readiness scoring, maturity classification, domain heatmaps, risks, and opportunities."],
                ["03", "Transform", "Executive-ready artifacts including dashboards, reports, decks, and 90-day plans."],
              ].map(([num, title, body]) => (
                <motion.div key={title} className="rounded-3xl border border-gray-100 p-8 shadow-sm bg-white" {...motionConfig}>
                  <div className="text-primary-teal font-extrabold text-sm mb-4">{num}</div>
                  <h3 className="text-2xl font-bold mb-4">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-24 bg-[#F5FAF9]">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...motionConfig}>
              <h2 className="text-4xl md:text-5xl font-bold">Five domains. One executive view.</h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                The assessment evaluates the organizational capabilities that determine whether AI initiatives create value
                or become another layer of operational complexity.
              </p>

              <div className="mt-8 grid gap-3">
                {domains.map((d) => (
                  <div key={d} className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl p-4">
                    <CheckCircle className="text-primary-teal" size={22} />
                    <span className="font-bold">{d}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div className="rounded-3xl bg-white border border-gray-100 p-6 shadow-xl" {...motionConfig}>
              <div className="text-sm font-bold text-gray-400 uppercase mb-4">Sample AI Readiness Heatmap™</div>
              {[
                ["Data", 50, "YELLOW"],
                ["Technology", 55, "YELLOW"],
                ["Governance", 65, "YELLOW"],
                ["Organization", 68, "YELLOW"],
                ["Delivery", 65, "YELLOW"],
              ].map(([name, score, status]) => (
                <div key={name} className="grid grid-cols-3 gap-4 items-center py-4 border-b last:border-b-0">
                  <div className="font-bold">{name}</div>
                  <div className="text-2xl font-extrabold text-dark-teal">{score}</div>
                  <div className="rounded-full bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1 text-center">{status}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section id="outputs" className="px-6 py-24 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div className="text-center mb-14" {...motionConfig}>
              <h2 className="text-4xl md:text-5xl font-bold">Executive outputs that move decisions.</h2>
              <p className="mt-5 text-lg text-gray-600 max-w-3xl mx-auto">
                This is not another survey. BioPM AI Readiness™ generates leadership-ready artifacts designed for
                governance reviews, steering committees, and budget discussions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {outputs.map((o) => (
                <motion.div key={o.title} className="rounded-3xl border border-gray-100 p-8 bg-white shadow-sm hover:shadow-xl transition-shadow" {...motionConfig}>
                  <div className="text-primary-teal mb-5">{o.icon}</div>
                  <h3 className="text-2xl font-bold mb-4">{o.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{o.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-24 bg-dark-teal text-white">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...motionConfig}>
              <div className="inline-flex px-4 py-2 rounded-full bg-primary-teal/15 text-primary-teal border border-primary-teal/30 text-sm font-bold mb-6">
                Launch Offering
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">AI Readiness Assessment™</h2>
              <p className="mt-6 text-white/70 text-lg leading-relaxed">
                A focused assessment and executive deliverable package for biotech organizations preparing to adopt,
                govern, or scale AI across drug development operations.
              </p>
            </motion.div>

            <motion.div className="rounded-3xl bg-white text-dark-teal p-8 shadow-2xl" {...motionConfig}>
              <div className="text-sm font-bold text-gray-400 uppercase">Starting at</div>
              <div className="text-5xl font-extrabold mt-2">$4,995</div>
              <div className="mt-2 text-gray-500">fixed assessment package</div>

              <div className="mt-8 grid gap-4">
                {[
                  "50-question AI readiness assessment",
                  "Executive Dashboard™",
                  "Executive Report™ PDF",
                  "Executive Deck™ PowerPoint",
                  "AI Readiness Heatmap™",
                  "90-Day Transformation Plan™",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="text-primary-teal" size={20} />
                    <span className="font-semibold">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href="mailto:info@biopm.com?subject=AI%20Readiness%20Assessment%20Inquiry"
                className="mt-8 inline-flex w-full justify-center items-center gap-2 px-8 py-4 rounded-full bg-primary-teal text-dark-teal font-bold no-underline"
              >
                Book Assessment <ArrowRight size={20} />
              </a>
            </motion.div>
          </div>
        </section>

        <section className="px-6 py-24 bg-white">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
            <motion.div {...motionConfig}>
              <Layers className="text-primary-teal mb-6" size={44} />
              <h2 className="text-4xl md:text-5xl font-bold">Built for the future of CurePath™.</h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                BioPM AI Readiness™ is the entry point. CurePath™ is the operating system for turning assessment findings
                into executable transformation programs, workstreams, milestones, risks, and executive visibility.
              </p>
              <a
                href="/#curepath"
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full border border-dark-teal text-dark-teal font-bold no-underline"
              >
                Learn About CurePath™ <ActivitySquare size={20} />
              </a>
            </motion.div>

            <motion.div className="rounded-3xl border border-gray-100 shadow-xl p-8 bg-[#F5FAF9]" {...motionConfig}>
              <div className="text-sm font-bold text-primary-teal uppercase mb-3">Request Assessment</div>
              <h3 className="text-3xl font-extrabold mb-4">Start the conversation.</h3>
              <p className="text-gray-600 mb-6">
                Tell us a little about your organization and current AI initiatives. BioPM will follow up to discuss fit, timing, and next steps.
              </p>

              <div className="grid gap-4">
                {[
                  ["name", "Full Name"],
                  ["company", "Company"],
                  ["title", "Title"],
                  ["email", "Email"],
                ].map(([key, label]) => (
                  <input
                    key={key}
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    placeholder={label}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-teal"
                  />
                ))}

                <textarea
                  value={form.initiative}
                  onChange={(e) => setForm({ ...form, initiative: e.target.value })}
                  placeholder="Current AI initiative or challenge"
                  rows="4"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-teal"
                />

                <button
                  type="button"
                  onClick={handleAssessmentSubmit}
                  className="inline-flex justify-center items-center gap-2 px-8 py-4 rounded-full bg-primary-teal text-dark-teal font-bold border-0 cursor-pointer"
                >
                  Submit Inquiry <ArrowRight size={20} />
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  );
}
